import { useEffect, useRef, useState, useImperativeHandle, forwardRef } from 'react';
import { loadGoogleMaps } from '@/lib/googleMapsLoader';
import { VehicleInfoWindow } from './VehicleInfoWindow';
import { GpsDevice } from '@/hooks/use-gps-devices';
import { Geofence } from '@/hooks/use-geofences';
import { toast } from 'sonner';

// Interface removida - usando GpsDevice do hook

interface ColoredRoutePoint {
  lat: number;
  lng: number;
  speed_kmh: number | null;
  fix_time: string;
}

interface GoogleLiveMapProps {
  devices: GpsDevice[];
  selectedDeviceId?: string;
  onDeviceSelect?: (deviceId: string) => void;
  onRouteDetailsClick?: (deviceId: string) => void;
  companyId?: string;
  routePolyline?: Array<{ lat: number; lng: number }>;
  coloredRoutePoints?: ColoredRoutePoint[];
  showStopMarkers?: boolean;
  replayMode?: boolean;
  replayPositions?: Array<{
    id: string;
    fix_time: string;
    lat: number;
    lng: number;
    speed_kmh: number | null;
    course_deg: number | null;
    ignition: boolean | null;
  }>;
  replayCurrentIndex?: number;
  geofences?: Geofence[];
  showGeofences?: boolean;
  drawingMode?: 'none' | 'circle' | 'polygon';
  onGeofenceDrawn?: (coordinates: any) => void;
}

export interface GoogleLiveMapRef {
  centerAndZoom: (lat: number, lng: number, zoom?: number) => void;
}

function getSpeedColor(speed: number | null): string {
  if (!speed || speed <= 5) return '#3B82F6'; // Azul - Parado
  if (speed <= 40) return '#22C55E'; // Verde - Normal
  if (speed <= 80) return '#EAB308'; // Amarelo - Rápido
  return '#EF4444'; // Vermelho - Crítico
}

function detectStops(points: ColoredRoutePoint[]): Array<{
  lat: number;
  lng: number;
  duration: number;
}> {
  const stops: Array<{lat: number, lng: number, duration: number}> = [];
  let stopStart: ColoredRoutePoint | null = null;
  let stopDuration = 0;

  points.forEach((point, index) => {
    if ((point.speed_kmh || 0) <= 5) {
      if (!stopStart) {
        stopStart = point;
      }
      if (index > 0 && points[index - 1]) {
        const timeDiff = new Date(point.fix_time).getTime() - new Date(points[index-1].fix_time).getTime();
        stopDuration += timeDiff / 60000; // converter para minutos
      }
    } else {
      if (stopStart && stopDuration >= 3) {
        stops.push({
          lat: stopStart.lat,
          lng: stopStart.lng,
          duration: Math.round(stopDuration)
        });
      }
      stopStart = null;
      stopDuration = 0;
    }
  });

  return stops;
}

const GoogleLiveMap = forwardRef<GoogleLiveMapRef, GoogleLiveMapProps>(({ 
  devices, 
  selectedDeviceId, 
  onDeviceSelect,
  onRouteDetailsClick,
  companyId,
  routePolyline,
  coloredRoutePoints,
  showStopMarkers,
  replayMode,
  replayPositions,
  replayCurrentIndex = 0,
  geofences = [],
  showGeofences = true,
  drawingMode = 'none',
  onGeofenceDrawn,
}, ref) => {
  const mapRef = useRef<HTMLDivElement>(null);
  const mapInstanceRef = useRef<google.maps.Map | null>(null);
  const [mapType, setMapType] = useState<'roadmap' | 'satellite' | 'hybrid'>('roadmap');
  const markersRef = useRef<Map<string, google.maps.Marker>>(new Map());
  const [infoWindowDevice, setInfoWindowDevice] = useState<GpsDevice | null>(null);
  const [isLoaded, setIsLoaded] = useState(false);
  const polylineRef = useRef<google.maps.Polyline | null>(null);
  const coloredPolylinesRef = useRef<google.maps.Polyline[]>([]);
  const stopMarkersRef = useRef<google.maps.Marker[]>([]);
  const replayMarkerRef = useRef<google.maps.Marker | null>(null);
  const geofenceOverlaysRef = useRef<Array<google.maps.Circle | google.maps.Polygon>>([]);
  const [lastFittedRoute, setLastFittedRoute] = useState<string>('');
  const [lastFittedColoredRoute, setLastFittedColoredRoute] = useState<string>('');
  
  // Drawing Manager refs
  const drawingManagerRef = useRef<google.maps.drawing.DrawingManager | null>(null);
  const activeCircleRef = useRef<google.maps.Circle | null>(null);
  const hasConfirmedRef = useRef(false);
  const [isPreviewMode, setIsPreviewMode] = useState(false);

  // Expose methods via ref
  useImperativeHandle(ref, () => ({
    centerAndZoom: (lat: number, lng: number, zoom: number = 15) => {
      if (mapInstanceRef.current) {
        mapInstanceRef.current.setCenter({ lat, lng });
        mapInstanceRef.current.setZoom(zoom);
      }
    },
    confirmCircle: () => {
      if (activeCircleRef.current && !hasConfirmedRef.current) {
        const center = activeCircleRef.current.getCenter();
        let radius = activeCircleRef.current.getRadius();
        
        radius = Math.max(20, radius);
        
        if (center) {
          activeCircleRef.current.setEditable(false);
          activeCircleRef.current.setOptions({
            strokeColor: '#10B981',
            strokeWeight: 3
          });
          
          onGeofenceDrawn?.({
            type: 'circle',
            center: { lat: center.lat(), lng: center.lng() },
            radius
          });
          
          hasConfirmedRef.current = true;
          
          if (mapInstanceRef.current) {
            mapInstanceRef.current.setOptions({
              draggable: true,
              disableDoubleClickZoom: false
            });
          }
          
          toast.success(`Geocerca circular criada! Raio: ${Math.round(radius)}m`);
          setIsPreviewMode(false);
          activeCircleRef.current = null;
        }
      }
    },
    cancelCircle: () => {
      if (activeCircleRef.current) {
        activeCircleRef.current.setMap(null);
        activeCircleRef.current = null;
        hasConfirmedRef.current = false;
        
        if (mapInstanceRef.current) {
          mapInstanceRef.current.setOptions({
            draggable: true,
            disableDoubleClickZoom: false
          });
        }
        
        toast.info('Desenho de geocerca cancelado');
        setIsPreviewMode(false);
      }
    }
  }));

  // Inicializar Google Maps
  useEffect(() => {
    loadGoogleMaps().then(() => {
      if (mapRef.current && !mapInstanceRef.current) {
        mapInstanceRef.current = new google.maps.Map(mapRef.current, {
          center: { lat: -14.235, lng: -51.9253 }, // Centro do Brasil
          zoom: 3, // Zoom amplo para mostrar todas as Américas
          mapTypeControl: false,
          streetViewControl: false,
          fullscreenControl: false,
          zoomControl: false,
          mapTypeId: mapType,
          gestureHandling: 'greedy', // Permite scroll sem Ctrl
        });
        
        setIsLoaded(true);
      }
    });
  }, []);

  // Atualizar tipo de mapa quando muda
  useEffect(() => {
    if (!mapInstanceRef.current) return;
    mapInstanceRef.current.setMapTypeId(mapType);
  }, [mapType]);

  // Atualizar marcadores quando devices mudam
  useEffect(() => {
    if (!isLoaded || !mapInstanceRef.current) return;

    const validDevices = devices.filter(d => d.lat && d.lon);

    // Criar/atualizar marcadores
    validDevices.forEach(device => {
      const position = { lat: device.lat, lng: device.lon };
      const existingMarker = markersRef.current.get(device.id);
      
      const isSelected = device.id === selectedDeviceId;
      
      if (existingMarker) {
        // Atualizar posição e ícone
        existingMarker.setPosition(position);
        existingMarker.setIcon(createCustomIcon(device, isSelected));
      } else {
        // Criar novo marcador
        const marker = new google.maps.Marker({
          position,
          map: mapInstanceRef.current,
          icon: createCustomIcon(device, isSelected),
          title: device.name,
          animation: isSelected ? google.maps.Animation.BOUNCE : undefined,
        });

        marker.addListener('click', () => {
          setInfoWindowDevice(device);
          onDeviceSelect?.(device.id);
        });

        markersRef.current.set(device.id, marker);
      }
    });

    // Remover marcadores de devices que não existem mais
    markersRef.current.forEach((marker, id) => {
      if (!validDevices.find(d => d.id === id)) {
        marker.setMap(null);
        markersRef.current.delete(id);
      }
    });
  }, [devices, isLoaded, onDeviceSelect, selectedDeviceId]);

  // Highlight do marcador selecionado
  useEffect(() => {
    if (!selectedDeviceId) return;
    
    // Atualizar ícone de todos os marcadores
    markersRef.current.forEach((marker, deviceId) => {
      const device = devices.find(d => d.id === deviceId);
      if (device) {
        const isSelected = deviceId === selectedDeviceId;
        marker.setIcon(createCustomIcon(device, isSelected));
        marker.setAnimation(isSelected ? google.maps.Animation.BOUNCE : undefined);
        
        // Remover animação após 2 segundos
        if (isSelected) {
          setTimeout(() => marker.setAnimation(null), 2000);
        }
      }
    });
    
    const device = devices.find(d => d.id === selectedDeviceId);
    const marker = markersRef.current.get(selectedDeviceId);
    if (marker && mapInstanceRef.current && device) {
      // Pan to selected device with smooth zoom
      mapInstanceRef.current.panTo(marker.getPosition()!);
      const currentZoom = mapInstanceRef.current.getZoom() || 15;
      if (currentZoom < 15) {
        mapInstanceRef.current.setZoom(15);
      }
      
      // Abrir InfoWindow React
      setInfoWindowDevice(device);
    }
  }, [selectedDeviceId, devices]);

  // Renderizar polyline quando routePolyline muda
  useEffect(() => {
    if (!mapInstanceRef.current) return;

    // Remover polyline anterior
    if (polylineRef.current) {
      polylineRef.current.setMap(null);
    }

    // Criar novo polyline se houver dados
    if (routePolyline && routePolyline.length > 0) {
      polylineRef.current = new google.maps.Polyline({
        path: routePolyline.map((p) => ({ lat: p.lat, lng: p.lng })),
        geodesic: true,
        strokeColor: '#3b82f6',
        strokeOpacity: 0.8,
        strokeWeight: 4,
        map: mapInstanceRef.current,
      });

      // Ajustar bounds APENAS se for uma rota diferente
      const routeKey = `${routePolyline.length}-${routePolyline[0]?.lat}-${routePolyline[0]?.lng}`;
      if (routeKey !== lastFittedRoute) {
        const bounds = new google.maps.LatLngBounds();
        routePolyline.forEach((p) => bounds.extend({ lat: p.lat, lng: p.lng }));
        if (!bounds.isEmpty()) {
          mapInstanceRef.current.fitBounds(bounds, 60);
        }
        setLastFittedRoute(routeKey);
      }
    } else {
      // Limpar controle quando polyline é removido
      setLastFittedRoute('');
    }
  }, [routePolyline, lastFittedRoute]);

  // Render colored polylines
  useEffect(() => {
    if (!mapInstanceRef.current) return;

    // Limpar polylines anteriores
    coloredPolylinesRef.current.forEach(p => p.setMap(null));
    coloredPolylinesRef.current = [];

    if (!coloredRoutePoints || coloredRoutePoints.length === 0) return;

    // Criar segmentos por cor
    let currentSegment: ColoredRoutePoint[] = [];
    let currentColor = '';

    coloredRoutePoints.forEach((point, index) => {
      const color = getSpeedColor(point.speed_kmh);
      
      if (index === 0) {
        currentSegment = [point];
        currentColor = color;
      } else if (color !== currentColor) {
        // Adicionar primeiro ponto do próximo segmento para continuidade
        currentSegment.push(point);
        
        // Criar polyline do segmento
        const polyline = new google.maps.Polyline({
          path: currentSegment.map(p => ({ lat: p.lat, lng: p.lng })),
          geodesic: true,
          strokeColor: currentColor,
          strokeOpacity: 0.9,
          strokeWeight: 5,
          map: mapInstanceRef.current,
        });
        coloredPolylinesRef.current.push(polyline);
        
        // Iniciar novo segmento
        currentSegment = [point];
        currentColor = color;
      } else {
        currentSegment.push(point);
      }
    });

    // Criar última polyline
    if (currentSegment.length > 0) {
      const polyline = new google.maps.Polyline({
        path: currentSegment.map(p => ({ lat: p.lat, lng: p.lng })),
        geodesic: true,
        strokeColor: currentColor,
        strokeOpacity: 0.9,
        strokeWeight: 5,
        map: mapInstanceRef.current,
      });
      coloredPolylinesRef.current.push(polyline);
    }

    // Ajustar bounds APENAS se for uma rota diferente
    if (coloredRoutePoints && coloredRoutePoints.length > 0) {
      const routeKey = `${coloredRoutePoints.length}-${coloredRoutePoints[0]?.lat}-${coloredRoutePoints[0]?.lng}`;
      if (routeKey !== lastFittedColoredRoute) {
        const bounds = new google.maps.LatLngBounds();
        coloredRoutePoints.forEach(p => bounds.extend({ lat: p.lat, lng: p.lng }));
        if (!bounds.isEmpty()) {
          mapInstanceRef.current.fitBounds(bounds, 80);
        }
        setLastFittedColoredRoute(routeKey);
      }
    } else {
      // Limpar controle quando polyline é removido
      setLastFittedColoredRoute('');
    }
  }, [coloredRoutePoints, lastFittedColoredRoute]);

  // Render stop markers
  useEffect(() => {
    if (!mapInstanceRef.current) return;

    // Limpar marcadores anteriores
    stopMarkersRef.current.forEach(m => m.setMap(null));
    stopMarkersRef.current = [];

    if (!showStopMarkers || !coloredRoutePoints || coloredRoutePoints.length === 0) return;

    const stops = detectStops(coloredRoutePoints);
    
    stops.forEach(stop => {
      const marker = new google.maps.Marker({
        position: { lat: stop.lat, lng: stop.lng },
        map: mapInstanceRef.current,
        icon: {
          path: google.maps.SymbolPath.CIRCLE,
          scale: 8,
          fillColor: '#DC2626',
          fillOpacity: 1,
          strokeColor: '#FFFFFF',
          strokeWeight: 2,
        },
        title: `Parada: ${stop.duration} min`,
        zIndex: 5000,
      });
      stopMarkersRef.current.push(marker);
    });

  }, [showStopMarkers, coloredRoutePoints]);

  // Replay marker management
  useEffect(() => {
    if (!mapInstanceRef.current || !replayMode || !replayPositions || replayPositions.length === 0) {
      if (replayMarkerRef.current) {
        replayMarkerRef.current.setMap(null);
        replayMarkerRef.current = null;
      }
      return;
    }

    const currentPos = replayPositions[replayCurrentIndex];
    const position = { lat: currentPos.lat, lng: currentPos.lng };

    const createReplayIcon = (courseDeg: number | null): google.maps.Symbol => ({
      path: 'M 0,-2 L 1.5,2 L 0,1 L -1.5,2 Z',
      fillColor: '#3b82f6',
      fillOpacity: 1,
      strokeColor: '#ffffff',
      strokeWeight: 2,
      scale: 8,
      rotation: courseDeg || 0,
      anchor: new google.maps.Point(0, 0),
    });

    if (replayMarkerRef.current) {
      replayMarkerRef.current.setPosition(position);
      replayMarkerRef.current.setIcon(createReplayIcon(currentPos.course_deg));
    } else {
      replayMarkerRef.current = new google.maps.Marker({
        position,
        map: mapInstanceRef.current,
        icon: createReplayIcon(currentPos.course_deg),
        title: 'Replay',
        zIndex: 10000,
      });
    }
  }, [replayMode, replayPositions, replayCurrentIndex]);

  // Adjust device markers opacity during replay
  useEffect(() => {
    markersRef.current.forEach(marker => {
      marker.setOpacity(replayMode ? 0.3 : 1.0);
    });
  }, [replayMode]);

  // Renderizar geocercas no mapa
  useEffect(() => {
    if (!mapInstanceRef.current || !geofences || !showGeofences) {
      // Limpar geocercas se showGeofences é false
      geofenceOverlaysRef.current.forEach(overlay => overlay.setMap(null));
      geofenceOverlaysRef.current = [];
      return;
    }

    // Limpar geocercas anteriores
    geofenceOverlaysRef.current.forEach(overlay => overlay.setMap(null));
    geofenceOverlaysRef.current = [];

    // Renderizar cada geocerca
    geofences.forEach(geofence => {
      if (geofence.type === 'circle') {
        const circle = new google.maps.Circle({
          strokeColor: geofence.active ? '#3B82F6' : '#9CA3AF',
          strokeOpacity: 0.8,
          strokeWeight: 2,
          fillColor: geofence.active ? '#3B82F6' : '#9CA3AF',
          fillOpacity: 0.15,
          map: mapInstanceRef.current,
          center: { 
            lat: geofence.coordinates.lat, 
            lng: geofence.coordinates.lng 
          },
          radius: geofence.coordinates.radius,
        });
        geofenceOverlaysRef.current.push(circle);
      } else if (geofence.type === 'polygon') {
        const polygon = new google.maps.Polygon({
          paths: geofence.coordinates.map((coord: any) => ({ 
            lat: coord.lat, 
            lng: coord.lng 
          })),
          strokeColor: geofence.active ? '#10B981' : '#9CA3AF',
          strokeOpacity: 0.8,
          strokeWeight: 2,
          fillColor: geofence.active ? '#10B981' : '#9CA3AF',
          fillOpacity: 0.15,
          map: mapInstanceRef.current,
        });
        geofenceOverlaysRef.current.push(polygon);
      }
    });
  }, [geofences, showGeofences]);

  // Cleanup drawing when mode changes
  useEffect(() => {
    if (drawingMode === 'none') {
      if (activeCircleRef.current) {
        activeCircleRef.current.setMap(null);
        activeCircleRef.current = null;
      }
      hasConfirmedRef.current = false;
      setIsPreviewMode(false);
    }
  }, [drawingMode]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (polylineRef.current) {
        polylineRef.current.setMap(null);
      }
      if (replayMarkerRef.current) {
        replayMarkerRef.current.setMap(null);
      }
      geofenceOverlaysRef.current.forEach(overlay => overlay.setMap(null));
    };
  }, []);

  return (
    <div className="relative w-full h-full">
      <div 
        ref={mapRef} 
        className="w-full h-screen" 
        style={{ cursor: drawingMode !== 'none' ? 'crosshair' : 'default' }}
      />
      
      {/* React InfoWindow customizado */}
      {infoWindowDevice && isLoaded && (
        <VehicleInfoWindow
          device={infoWindowDevice}
          onClose={() => setInfoWindowDevice(null)}
          onDetailsClick={() => {
            if (onRouteDetailsClick) {
              onRouteDetailsClick(infoWindowDevice.id);
            }
            setInfoWindowDevice(null);
          }}
          companyId={companyId}
        />
      )}
      
      {/* Controles de tipo de mapa - Miniaturas visuais */}
      <div className="fixed bottom-20 right-6 z-20 flex gap-1.5 bg-background/95 backdrop-blur-md rounded-lg shadow-lg border p-1.5">
        {/* Miniatura Padrão (Roadmap) */}
        <button
          onClick={() => setMapType('roadmap')}
          className={`relative w-12 h-12 rounded-md overflow-hidden border-2 transition-all ${
            mapType === 'roadmap' 
              ? 'border-primary ring-2 ring-primary/50 scale-105' 
              : 'border-border/50 opacity-70 hover:opacity-100 hover:border-primary/50'
          }`}
          title="Mapa Padrão"
        >
          <div className="w-full h-full bg-gradient-to-br from-blue-100 via-white to-green-100" />
          <span className="absolute bottom-0 inset-x-0 text-[9px] font-semibold bg-black/60 text-white text-center py-0.5">
            Padrão
          </span>
        </button>

        {/* Miniatura Satélite */}
        <button
          onClick={() => setMapType('satellite')}
          className={`relative w-12 h-12 rounded-md overflow-hidden border-2 transition-all ${
            mapType === 'satellite' 
              ? 'border-primary ring-2 ring-primary/50 scale-105' 
              : 'border-border/50 opacity-70 hover:opacity-100 hover:border-primary/50'
          }`}
          title="Satélite"
        >
          <div className="w-full h-full bg-gradient-to-br from-green-800 via-green-600 to-blue-900" />
          <span className="absolute bottom-0 inset-x-0 text-[9px] font-semibold bg-black/60 text-white text-center py-0.5">
            Satélite
          </span>
        </button>

        {/* Miniatura Híbrido */}
        <button
          onClick={() => setMapType('hybrid')}
          className={`relative w-12 h-12 rounded-md overflow-hidden border-2 transition-all ${
            mapType === 'hybrid' 
              ? 'border-primary ring-2 ring-primary/50 scale-105' 
              : 'border-border/50 opacity-70 hover:opacity-100 hover:border-primary/50'
          }`}
          title="Híbrido"
        >
          <div className="w-full h-full bg-gradient-to-br from-green-800 via-green-600 to-blue-800">
            <svg className="absolute inset-0 w-full h-full opacity-40" viewBox="0 0 100 100">
              <line x1="0" y1="50" x2="100" y2="50" stroke="white" strokeWidth="2"/>
              <line x1="50" y1="0" x2="50" y2="100" stroke="white" strokeWidth="2"/>
            </svg>
          </div>
          <span className="absolute bottom-0 inset-x-0 text-[9px] font-semibold bg-black/60 text-white text-center py-0.5">
            Híbrido
          </span>
        </button>
      </div>
    </div>
  );
});

GoogleLiveMap.displayName = 'GoogleLiveMap';

export default GoogleLiveMap;

// Criar ícone customizado baseado no status
const createCustomIcon = (device: GpsDevice, isSelected: boolean = false): google.maps.Symbol => {
  const colors = {
    moving: '#10b981',
    idle: '#f59e0b',
    offline: '#6b7280',
    sleep: '#8b5cf6',
    unknown: '#9ca3af',
  };
  
  const color = colors[device.status] || '#9ca3af';
  
  return {
    path: google.maps.SymbolPath.CIRCLE,
    scale: isSelected ? 16 : 12,
    fillColor: color,
    fillOpacity: 1,
    strokeColor: '#ffffff',
    strokeWeight: isSelected ? 4 : 3,
  };
};

// Criar conteúdo da InfoWindow
const createInfoWindowContent = (device: GpsDevice): string => {
  const statusEmojis: Record<string, string> = {
    moving: '🟢',
    idle: '🟡',
    offline: '🔴',
    sleep: '🟣',
    unknown: '⚪',
  };

  const statusLabels: Record<string, string> = {
    moving: 'Em Movimento',
    idle: 'Parado',
    offline: 'Offline',
    sleep: 'Inativo',
    unknown: 'Desconhecido',
  };

  const statusEmoji = statusEmojis[device.status] || statusEmojis.unknown;
  const statusLabel = statusLabels[device.status] || statusLabels.unknown;
  
  const vehicleTitle = device.vehicle?.model 
    ? `${device.vehicle.make || ''} ${device.vehicle.model}`.trim()
    : device.name;

  return `
    <div style="padding: 16px; min-width: 250px; font-family: system-ui, -apple-system, sans-serif;">
      <!-- Ícone do carro -->
      <div style="text-align: center; margin-bottom: 12px;">
        <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" style="color: #3B82F6; margin: 0 auto;">
          <path d="M5 17h-2v-2m2 2v2m0-2h4m10-2h2v-2m-2 2v2m0-2h-4m-10-6l1.5-4.5A2 2 0 0 1 8.5 4h7a2 2 0 0 1 1.937 1.5L19 10m-14 0h14m-14 0v4m14-4v4m-7-2h.01"/>
        </svg>
      </div>
      
      <!-- Título -->
      <h3 style="margin: 0 0 4px 0; font-size: 18px; font-weight: 700; color: #1F2937; text-align: center;">
        ${vehicleTitle}
      </h3>
      
      <!-- Placa -->
      ${device.vehicle?.plate ? `
        <p style="margin: 0 0 12px 0; font-size: 13px; color: #6B7280; text-align: center; font-family: monospace; font-weight: 600;">
          ${device.vehicle.plate}
        </p>
      ` : '<div style="margin-bottom: 12px;"></div>'}
      
      <div style="border-top: 1px solid #E5E7EB; padding-top: 12px;">
        <!-- Status -->
        <div style="display: flex; align-items: center; gap: 8px; margin-bottom: 8px;">
          <span style="font-size: 16px;">${statusEmoji}</span>
          <span style="font-size: 14px; font-weight: 600; color: #374151;">${statusLabel}</span>
        </div>
        
        <!-- Ignição -->
        <p style="margin: 6px 0; font-size: 14px; color: #6B7280;">
          ${device.ignition ? '🔑 Motor ligado' : '🔒 Motor desligado'}
        </p>
        
        <!-- Velocidade -->
        ${device.speed_kmh ? `
          <p style="margin: 6px 0; font-size: 14px; color: #6B7280;">
            🏃 ${Math.round(device.speed_kmh)} km/h
          </p>
        ` : ''}
        
        <!-- Última atualização -->
        ${device.last_signal_at ? `
          <p style="margin: 10px 0 0 0; font-size: 12px; color: #9CA3AF; padding-top: 8px; border-top: 1px solid #E5E7EB;">
            🕐 ${getTimeSince(device.last_signal_at)}
          </p>
        ` : ''}
      </div>
    </div>
  `;
};

function getTimeSince(timestamp: string): string {
  const now = new Date();
  const signalTime = new Date(timestamp);
  const diffMins = Math.floor((now.getTime() - signalTime.getTime()) / 60000);
  if (diffMins < 1) return 'Agora';
  if (diffMins < 60) return `${diffMins}min atrás`;
  const diffHours = Math.floor(diffMins / 60);
  if (diffHours < 24) return `${diffHours}h atrás`;
  const diffDays = Math.floor(diffHours / 24);
  return `${diffDays}d atrás`;
}
